import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useEdgesState,
  MarkerType,
  applyNodeChanges
} from 'react-flow-renderer';
import NodePanel from '../components/NodePanel';
import PropertyPanel from '../components/PropertyPanel';
import MessageNode from '../components/nodes/MessageNode';
import DecisionNode from '../components/nodes/DecisionNode';
import ApiNode from '../components/nodes/ApiNode';
import FaqNode from '../components/nodes/FaqNode';
import RasaIntentManager from '../components/RasaIntentManager';
import FileService from '../services/FileService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFlowContext } from '../context/FlowContext';

// Node types for ReactFlow
const nodeTypes = {
  messageNode: MessageNode,
  decisionNode: DecisionNode,
  apiNode: ApiNode,
  faqNode: FaqNode,
};

const API_BASE = "http://10.0.9.7:3001";

const FlowBuilder = forwardRef(({
  isIntentManagerOpen,
  setIsIntentManagerOpen,
  startNode,
  setStartNode,
  nodes,
  setNodes,
  onSetLoadFlow
}, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, flowId, setFlowId, flowName, setFlowName, notification, hideNotification, userGroup, userOrg, autoSaveEnabled } = useFlowContext();
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Sync flowId from location state to context if needed
  useEffect(() => {
    if (location.state && location.state.flowId && !flowId) {
      console.log('Syncing flowId from state:', location.state.flowId);
      if (typeof setFlowId === 'function') {
        setFlowId(location.state.flowId);
      }
    }
  }, [location.state, flowId, setFlowId]);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [rasaIntents, setRasaIntents] = useState([
    { name: 'YES', examples: ['Yes', 'Yep', 'Sure', 'Absolutely'] },
    { name: 'NO', examples: ['No', 'Nope', 'Not really', 'No thanks'] },
    { name: 'SPEC', examples: ['I want a laptop', 'I need a desktop', 'Looking for a computer'] }
  ]);
  const [rasaSlots, setRasaSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load RASA data on initial mount
  useEffect(() => {
    const loadRasaData = async () => {
      try {
        console.log('FlowBuilder: Loading merged RASA data...');
        const data = await FileService.loadRasaData(userId);
        if (data) {
          if (data.intents && Array.isArray(data.intents)) {
            setRasaIntents(data.intents);
          } else {
            setRasaIntents([]);
          }
          if (data.slots && Array.isArray(data.slots)) {
            setRasaSlots(data.slots);
            console.log('FlowBuilder: RASA slots loaded into state:', data.slots);
          } else {
            setRasaSlots([]);
          }
        } else {
          // Initialize with empty arrays if no data
          setRasaIntents([]);
          setRasaSlots([]);
        }
      } catch (error) {
        console.error('FlowBuilder: Error loading initial RASA data:', error);
        // Initialize with empty arrays on error instead of showing alert
        setRasaIntents([]);
        setRasaSlots([]);
      }
    };

    // Only load RASA data if we have userId
    if (userId) {
      loadRasaData();
    }
  }, [userId]);

  // Initialize flow name for new flows
  useEffect(() => {
    if (flowId === 'new' && (!flowName || flowName === 'Untitled Flow')) {
      fetch(`${API_BASE}/api/flows/${userId}/next-untitled`)
        .then(res => res.json())
        .then(data => {
          if (data.nextName) {
            setFlowName(data.nextName);
          }
        })
        .catch(err => console.error('Error fetching next untitled name:', err));
    }
  }, [flowId, userId]);

  // Unsaved changes prompt
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // If auto-save is on, we still prompt if it's dirty, 
      // because the user might close the tab before the 5s timer hits.
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Auto-save on unmount (navigation within the app)
  useEffect(() => {
    return () => {
      // This runs when the component unmounts (e.g. navigating back to dashboard)
      if (autoSaveEnabled && isDirty && nodes.length > 0) {
        console.log('Component unmounting, triggering final auto-save...');
        saveFlow({ isAutoSave: true });
      }
    };
  }, [isDirty, autoSaveEnabled, nodes, edges, rasaIntents, rasaSlots, flowName]);

  // Auto-save logic
  useEffect(() => {
    if (!autoSaveEnabled || !isDirty || Object.keys(nodes).length === 0) return;

    const timer = setTimeout(() => {
      console.log('Auto-saving flow...');
      saveFlow({ isAutoSave: true }); // Pass isAutoSave flag
    }, 5000); // 5 second debounce

    return () => clearTimeout(timer);
  }, [nodes, edges, autoSaveEnabled, isDirty]);

  // Create a reference object to track node positions for proper spacing
  const [nodePositions] = useState({
    lastX: 50,
    lastY: 50,
    rowHeight: 150,
    colWidth: 300
  });

  // Process flow data for the canvas
  const processFlowData = useCallback((flowData) => {
    if (!flowData || !flowData.nodes) {
      console.error('Invalid flow data:', flowData);
      alert('The flow data is invalid or empty');
      return;
    }
    console.log('FlowBuilder: Processing flow data for canvas...', flowData);
    try {
      const nodesObj = flowData.nodes;
      const labelToId = new Map();

      // First pass: Create node IDs and populate the map of labels to new IDs
      Object.keys(nodesObj).forEach(nodeName => {
        const nodeData = nodesObj[nodeName];
        const nodeId = `${nodeData.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        labelToId.set(nodeName, nodeId);
      });

      // Second pass: Create the nodes for React Flow, using the map to link them correctly
      const newNodes = Object.entries(nodesObj).map(([nodeName, nodeData]) => {
        const nodeId = labelToId.get(nodeName);
        const newNodeData = { ...nodeData };

        // Convert `next` properties from labels to the newly generated node IDs
        if (newNodeData.next) {
          newNodeData.next = labelToId.get(newNodeData.next) || null;
        }

        // Convert `conditions.next` properties from labels to IDs
        if (newNodeData.conditions && Array.isArray(newNodeData.conditions)) {
          newNodeData.conditions = newNodeData.conditions.map(condition => ({
            ...condition,
            next: condition.next ? (labelToId.get(condition.next) || null) : null,
          }));
        }

        return {
          id: nodeId,
          type: `${nodeData.type}Node`,
          position: nodeData.position || { x: 100, y: 100 },
          data: {
            ...newNodeData,
            label: nodeName,
            nodeId: nodeId,
            nodeType: nodeData.type,
          },
        };
      });

      // Translate the `edges` from the JSON file using the label-to-ID map
      const newEdges = (flowData.edges || []).map(edge => {
        const sourceId = labelToId.get(edge.source);
        const targetId = labelToId.get(edge.target);

        if (!sourceId || !targetId) {
          console.warn(`Could not map edge from ${edge.source} to ${edge.target}`);
          return null;
        }

        return {
          ...edge,
          id: edge.id || `reactflow__edge-${sourceId}${edge.sourceHandle || ''}-${targetId}${edge.targetHandle || ''}`,
          source: sourceId,
          target: targetId,
        };
      }).filter(Boolean);

      setNodes(newNodes);
      setEdges(newEdges);

      // Update startNode to use the new ID instead of the label
      if (flowData.startNode) {
        const startNodeId = labelToId.get(flowData.startNode);
        if (startNodeId) {
          setStartNode(startNodeId);
        }
      }
    } catch (error) {
      console.error('Failed to process flow data:', error);
      alert(`Error processing flow data: ${error.message}`);
    }
  }, [setNodes, setStartNode, setEdges]);

  // Load a flow from server - will be passed to Header
  const loadFlowFromServer = useCallback(async (flow) => {
    try {
      // If flowData is present (from FlowSelector), use it directly
      if (flow.flowData) {
        setFlowName(flow.name || flow.filename.replace('.json', '').replace(/_/g, ' '));
        processFlowData(flow.flowData);
        return;
      }
      const filename = flow.filename;
      if (!filename) {
        console.error('No filename provided for loading flow');
        alert('Failed to load flow: No filename provided');
        return;
      }
      // Fetch the flow data from the flowDB directory (GUI format)
      console.log(`Fetching flow "${filename}" from flowDB directory...`);
      const flowData = await FileService.getFlowDB(userId, filename);
      setFlowName(flow.name || filename.replace('.json', '').replace(/_/g, ' '));
      processFlowData(flowData);
    } catch (err) {
      console.error('Error loading flow:', err);
      alert(`Failed to load flow: ${err.message}`);
    }
  }, [setFlowName, processFlowData]);

  // Register the load flow function with the parent component
  useEffect(() => {
    console.log('Registering load flow function');
    if (onSetLoadFlow) {
      onSetLoadFlow(loadFlowFromServer);
    }
  }, [onSetLoadFlow, loadFlowFromServer]);

  // On mount, load flow from navigation state only
  useEffect(() => {
    setLoading(true);
    let nameToSet = '';
    if (location.state && location.state.flowData) {

      if (location.state.flowData && location.state.flowData.nodes) {
        processFlowData(location.state.flowData);
      }
      // Set flow name from navigation state, flowData, or fallback
      if (location.state.flowName) {
        nameToSet = location.state.flowName;
      } else if (location.state.flowData.flowName) {
        nameToSet = location.state.flowData.flowName;
      } else {
        nameToSet = '';
      }
      setFlowName && setFlowName(nameToSet);
      setFlowName(nameToSet);
      setLoading(false);
    } else {
      setLoading(false);
      alert('No flow data found. Please select a flow from the dashboard.');
    }
  }, [setFlowName, processFlowData, location.state]);

  // On mount, set flowName to empty if not loaded from file
  useEffect(() => {
    if (!location.state) {
      setFlowName && setFlowName("");
    }
  }, [location.state, setFlowName]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    saveFlow: (saveData) => {
      saveFlow(saveData);
    },
    getRasaIntents: () => {
      return rasaIntents;
    }
  }));

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    // Only update if the node selection has changed
    if (!selectedNode || selectedNode.id !== node.id) {
      setSelectedNode(node);

      // Update node style to show it's selected
      setNodes(nds =>
        nds.map(n => ({
          ...n,
          selected: n.id === node.id,
          style: {
            ...n.style,
            borderWidth: n.id === node.id ? '3px' : '1px',
            boxShadow: n.id === node.id ? '0 0 0 2px #6a1b9a' : n.style?.boxShadow
          }
        }))
      );
    }
  }, [selectedNode, setNodes]);

  // Handle connection between nodes
  const onConnect = useCallback((params) => {
    setIsDirty(true);
    setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: '#6a1b9a' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#6a1b9a' }
    }, eds));
  }, [setEdges]);

  // Add new node to canvas
  const onAddNode = useCallback((nodeType) => {
    setIsDirty(true);
    const position = { x: 100, y: 100 }; // Default position for new nodes

    const newNode = {
      id: `${nodeType}_${Date.now()}`,
      type: `${nodeType}Node`,
      data: {
        label: `New ${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Node`,
        nodeType
      },
      position,
      style: {
        border: '1px solid #6a1b9a',
        borderRadius: '8px',
        padding: '10px'
      }
    };

    setNodes((nds) => nds.concat(newNode));
  }, [setNodes]);

  // Update node properties
  const onUpdateNodeData = useCallback((id, newData) => {
    setIsDirty(true);
    setNodes((nds) => {
      const updatedNodes = nds.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      );
      // Also update selectedNode if it's the one being edited
      if (selectedNode && selectedNode.id === id) {
        const updatedNode = updatedNodes.find((n) => n.id === id);
        setSelectedNode(updatedNode);
      }
      return updatedNodes;
    });
  }, [setNodes, selectedNode]);

  // Update connections when node references change
  useEffect(() => {
    // Only run if we have nodes
    if (nodes.length === 0) return;

    // Helper function to create an edge ID
    const createEdgeId = (source, target, label) => {
      return `${source}-${target}-${label || 'default'}`;
    };

    // Create a map of current edges for comparison
    const currentEdgeMap = new Map();
    edges.forEach(edge => {
      currentEdgeMap.set(createEdgeId(edge.source, edge.target, edge.label), edge);
    });

    // Create a set of new edges
    const newEdges = [];
    const seenEdges = new Set();

    // Helper function to add an edge
    const addConnectionEdge = (source, target, label) => {
      if (!source || !target) return;

      const edgeId = createEdgeId(source, target, label);
      if (seenEdges.has(edgeId)) return;

      seenEdges.add(edgeId);

      // If this edge already exists, keep it as is
      if (currentEdgeMap.has(edgeId)) {
        newEdges.push(currentEdgeMap.get(edgeId));
        return;
      }

      // Otherwise, create a new edge
      newEdges.push({
        id: edgeId,
        source,
        target,
        label,
        animated: true,
        style: { stroke: '#6a1b9a' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#6a1b9a' }
      });
    };

    // Process each node to create edges based on their next property
    nodes.forEach(node => {
      const { id, data, type } = node;
      const nodeType = type.replace('Node', '');

      if (nodeType === 'decision') {
        if (data.captureResponse) {
          // For captureResponse nodes, create edge to next node
          if (data.next) {
            addConnectionEdge(id, data.next, 'next');
          }
        } else if (data.learnResponse) {
          // For learnResponse nodes, handle connections based on conditions
          if (data.conditions && Array.isArray(data.conditions)) {
            data.conditions.forEach(condition => {
              if (condition.next) {
                addConnectionEdge(id, condition.next, condition.condition);
              }
            });
          }
        }

        // Handle default next for decision nodes
        if (data.defaultNext) {
          addConnectionEdge(id, data.defaultNext, 'default');
        }

        // Handle FAQ connection
        if (data.useFaq) {
          // Find FAQ node
          const faqNode = nodes.find(n => n.type === 'faqNode');
          if (faqNode) {
            addConnectionEdge(id, faqNode.id, 'FAQ');
          }
        }
      } else {
        // For other node types with direct next property
        if (data.next) {
          addConnectionEdge(id, data.next, '');
        }
      }
    });

    // Only update edges if they've actually changed
    const edgesChanged = edges.length !== newEdges.length ||
      newEdges.some(newEdge => !currentEdgeMap.has(newEdge.id));

    if (edgesChanged) {
      setEdges(newEdges);
    }
  }, [nodes, edges, setEdges]);

  // Update startNode when nodes change (default to first node)
  useEffect(() => {
    if (nodes.length > 0 && !startNode) {
      setStartNode(nodes[0].id);
    }
  }, [nodes, startNode, setStartNode]);



  // Save flow as JSON to both flowDB and FLOWS
  const saveFlow = async (saveData = {}) => {
    const { flow_active, target_user_id, isAutoSave = false, overwrite = false } = saveData;

    // Prevent saving empty flows
    if (nodes.length === 0) {
      if (!isAutoSave) alert('Cannot save an empty flow. Please add at least one node.');
      return;
    }

    const nameToUse = flowName;
    if (!nameToUse || !nameToUse.trim()) {
      if (!isAutoSave) alert('Please enter a name for the flow before saving.');
      return;
    }

    // Validate name for special characters that might break shell commands
    const nameRegex = /^[a-zA-Z0-9 _-]+$/;
    if (!nameRegex.test(nameToUse)) {
      if (!isAutoSave) alert('Flow name contains special characters (like parentheses) which may cause issues. Please use only letters, numbers, spaces, underscores, and hyphens.');
      return;
    }
    if (!userId || !flowId) {
      if (!isAutoSave) {
        alert('Invalid user or flow. Please return to dashboard and start again.');
        navigate('/');
      }
      return;
    }

    // --- FIX: Add validation to ensure all nodes have a name ---
    const unlabeledNode = nodes.find(n => !n.data.label || !n.data.label.trim());
    if (unlabeledNode) {
      alert(`A node is missing its name. Please ensure all nodes are named before saving.`);
      // Optional: select the unlabeled node to help the user find it
      setSelectedNode(unlabeledNode);
      return;
    }
    // --- End of FIX ---

    const idToName = {};
    nodes.forEach(node => {
      idToName[node.id] = node.data.label || node.id;
    });
    const guiNodes = {};
    nodes.forEach(node => {
      const label = node.data.label;
      const nodeData = { ...node.data };
      if (nodeData.next) {
        nodeData.next = idToName[nodeData.next] || nodeData.next;
      }
      if (nodeData.conditions && Array.isArray(nodeData.conditions)) {
        nodeData.conditions = nodeData.conditions.map(condition => ({
          ...condition,
          next: condition.next ? (idToName[condition.next] || condition.next) : null
        }));
      }
      // Clean up mappings for GUI JSON to avoid empty/null values being saved
      if (nodeData.saveResponse && Array.isArray(nodeData.saveResponseMappings)) {
        nodeData.saveResponseMappings = nodeData.saveResponseMappings.map(m => ({
          responseType: m.responseType || null,
          infoField: m.infoField || null
        }));
      }
      guiNodes[label] = {
        ...nodeData,
        position: node.position,
        type: node.type.replace('Node', ''),
        nodeType: node.type.replace('Node', ''),
      };
    });
    const guiEdges = edges.map(edge => ({
      ...edge,
      source: idToName[edge.source] || edge.source,
      target: idToName[edge.target] || edge.target,
    }));
    const guiData = {
      nodes: guiNodes,
      edges: guiEdges,
      startNode: idToName[startNode] || startNode,
      intents: rasaIntents,
      slots: rasaSlots
    };

    // Fetch FAQ file path and add to JSON if exists
    let faqFilePath = null;
    try {
      const resFaq = await fetch(`${API_BASE}/api/faq/${userId}/${flowId}`);
      const faqData = await resFaq.json();
      if (faqData.filePath) {
        faqFilePath = faqData.filePath;
        guiData.faqFilePath = faqFilePath;
      }
    } catch (e) { /* ignore FAQ errors */ }

    // Save to backend with versioning
    try {
      const res = await fetch(`${API_BASE}/api/flows/${userId}/${flowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_name: nameToUse,
          data: guiData,
          flow_active: flow_active,
          target_user_id: target_user_id,
          organisation_name: userOrg,
          overwrite: overwrite,
          userGroup: userGroup
        })
      });

      if (res.ok) {
        const result = await res.json();
        setIsDirty(false);
        if (flowId === 'new') {
          setFlowId(result.flow_id);
          if (result.flow_name) {
            setFlowName(result.flow_name);
          }
        }
        if (!isAutoSave) {
          alert('Flow saved successfully!');
        } else {
          console.log('Flow auto-saved.');
        }
      } else if (res.status === 409) {
        // Handle duplicate flow name error
        const errorData = await res.json();
        if (!isAutoSave) {
          if (errorData.conflictType === 'org' && window.confirm(errorData.error)) {
            // Retry with overwrite
            return saveFlow({ ...saveData, overwrite: true });
          } else {
            alert(errorData.error || 'A flow with this name already exists. Please choose a different name.');
          }
        }
      } else {
        const errorText = await res.text();
        if (!isAutoSave) alert(`Error saving flow: ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      if (!isAutoSave) alert('Error saving flow. Please try again.');
    }
  };

  // Save RASA intents and slots
  const handleRasaSave = ({ intents, slots }, shouldTrain = false) => {
    // Update the state with new intents and slots
    setRasaIntents(intents);
    setRasaSlots(slots);

    if (shouldTrain) {
      // Format RASA training text
      const rasaText = generateRasaTrainingText(intents, slots);

      // Save to RASA file
      FileService.saveRasaFile(flowName, rasaText).then(() => {
        alert(`RASA training file saved as "RASA_${flowName}.txt"`);
        setIsIntentManagerOpen(false);
      }).catch(error => {
        alert(`Error saving RASA training file: ${error.message}`);
      });
    } else {
      setIsIntentManagerOpen(false);
    }
  };

  // Generate RASA training text
  const generateRasaTrainingText = (intents, slots) => {
    let text = 'intents:\n';
    text += intents.map(intent => intent.intent || intent.name).join('\n') + '\n\n';

    text += 'slots:\n';
    text += slots.map(slot => slot.slot || slot.name).join('\n') + '\n\n';

    // Add intent examples
    text += intents.map(intent => {
      return `${intent.intent || intent.name}\n${intent.examples.map(ex => `- ${ex}`).join('\n')}\n`;
    }).join('\n');

    return text;
  };

  const onDeleteNode = useCallback(() => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
      setSelectedNode(null);
    }
  }, [selectedNode, setNodes]);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updated = applyNodeChanges(changes, nds);
      const isMeaningful = changes.some(c => ['position', 'data', 'dimensions', 'remove', 'add'].includes(c.type));
      if (isMeaningful) setIsDirty(true);
      return updated;
    });
  }, [setNodes]);

  if (loading) return <div>Loading flow...</div>;

  return (
    <div className="flow-builder">

      {(userGroup || 'user').toLowerCase() !== 'user' && <NodePanel onAddNode={onAddNode} />}

      <div className="flow-canvas">
        <div className="flow-controls">
          <label className="mini-map-toggle">
            <input
              type="checkbox"
              checked={showMiniMap}
              onChange={(e) => setShowMiniMap(e.target.checked)}
            />
            Show Mini Map
          </label>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid={true}
          snapGrid={[20, 20]}
          defaultZoom={0.8}
        >
          <Controls />
          {showMiniMap && (
            <MiniMap
              nodeStrokeColor="#6a1b9a"
              nodeColor="#f3e5f5"
              nodeBorderRadius={8}
            />
          )}
          <Background color="#ccc" gap={16} />
        </ReactFlow>
      </div>

      <PropertyPanel
        selectedNode={selectedNode}
        onUpdateNodeData={onUpdateNodeData}
        rasaIntents={rasaIntents}
        rasaSlots={rasaSlots}
        onOpenIntentManager={() => setIsIntentManagerOpen(true)}
        allNodes={nodes}
        onDeleteNode={onDeleteNode}
        userGroup={userGroup}
      />

      {isIntentManagerOpen && (
        <RasaIntentManager
          onSave={handleRasaSave}
          onClose={() => setIsIntentManagerOpen(false)}
        />
      )}
    </div>
  );
});

// Add styles at the end of the file
const styles = `
.flow-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
  background: white;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mini-map-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.mini-map-toggle input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}
`;

// Add style tag to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default FlowBuilder;


